import React from 'react';
import styled from '@emotion/styled';
import Button from 'components/button/button';
import Text from 'components/text';
import { Theme } from '@seedlang/constants';
import { pixify } from '@seedlang/utils';

const FormContent = styled.div`
  padding: 0 20px 20px;
  @media (max-width: 500px) {
    padding: 0 12px 12px;
  }
`;

const ChoicesRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const Choice = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
  text-align: left;
  background: ${props => props.selected ? Theme.blue : '#fff'};
  color: ${props => props.selected ? '#fff' : '#000'};
  &:hover {
    background: ${props => props.selected ? Theme.blue : '#f5f5f5'};
  }
`;

const ActionsRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  width: 100%;
`;

const NotesArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
`;

const SourceInChoice = styled.span`
  font-style: italic;
`;

const BreadcrumbSlot = styled.div`
  margin-bottom: 0;
`;

const StepTitle = styled.h3`
  padding: 20px 0 10px 0;
`;

const CommentLink = styled.button`
  border: 0;
  background: none;
  color: ${Theme.blue};
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
`;

const BreadcrumbBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 4px;
  margin-top: 10px;
  padding: 10px 12px;
  background: #f7f7f7;
  border-radius: 5px;
  border: 1px solid #eaeaea;
  font-size: 13px;
  line-height: 1.45;
  color: #444;
  box-sizing: border-box;
`;

const BreadcrumbSep = styled.span`
  margin: 0 6px;
  color: #999;
  user-select: none;
`;

class ReportMeaningsForm extends React.Component {
  get steps() { return this.props.steps; }
  get step() { return this.props.step; }
  get selectedMeaning() { return this.props.selectedMeaning; }
  get wordLevelReasonCode() { return this.props.wordLevelReasonCode; }
  get selectedReasonCodes() { return this.props.selectedReasonCodes; }
  get notes() { return this.props.notes; }
  set notes(value) { this.props.setNotes(value); }
  get focusedTranslationSourceIds() { return this.props.focusedTranslationSourceIds; }
  get focusedSentenceIds() { return this.props.focusedSentenceIds; }
  get focusedSynonymIds() { return this.props.focusedSynonymIds; }
  get submitting() { return this.props.submitting; }
  get submitError() { return this.props.submitError; }
  get meaningFlagHubFocusChoices() { return this.props.meaningFlagHubFocusChoices; }
  get meaningFlagMeaningIssues() { return this.props.meaningFlagMeaningIssues; }
  get meaningFlagWordLevelIssues() { return this.props.meaningFlagWordLevelIssues; }
  get notesPromptText() { return this.props.notesPromptText; }
  get thankYouMessageText() { return this.props.thankYouMessageText; }
  get reportingBreadcrumbWordSegment() { return this.props.reportingBreadcrumbWordSegment; }
  get reportingBreadcrumbMeaningSegment() { return this.props.reportingBreadcrumbMeaningSegment; }
  get reportingBreadcrumbSubstepSegment() { return this.props.reportingBreadcrumbSubstepSegment; }
  get reportingBreadcrumbWordLevelIssueSegment() { return this.props.reportingBreadcrumbWordLevelIssueSegment; }
  get meaningsSorted() { return this.props.meaningsSorted; }
  get hasFirstStepChoice() { return this.props.hasFirstStepChoice; }
  get hasCurrentStepSelection() { return this.props.hasCurrentStepSelection; }
  get canSubmitCurrentStep() { return this.props.canSubmitCurrentStep; }
  get close() { return this.props.close; }
  get finishThankYou() { return this.props.finishThankYou; }
  get selectMeaning() { return this.props.selectMeaning; }
  get selectWordLevel() { return this.props.selectWordLevel; }
  get toggleReason() { return this.props.toggleReason; }
  get openTranslationPickStep() { return this.props.openTranslationPickStep; }
  get openExampleSentencePickStep() { return this.props.openExampleSentencePickStep; }
  get openSynonymPickStep() { return this.props.openSynonymPickStep; }
  get setFocusedTranslationSource() { return this.props.setFocusedTranslationSource; }
  get setFocusedSentence() { return this.props.setFocusedSentence; }
  get setFocusedSynonym() { return this.props.setFocusedSynonym; }
  get goBack() { return this.props.goBack; }
  get submitMeaningFlag() { return this.props.submitMeaningFlag; }
  get submitWordLevel() { return this.props.submitWordLevel; }
  get getTranslationSourceDisplayText() { return this.props.getTranslationSourceDisplayText; }
  get getSentenceSourceForUser() { return this.props.getSentenceSourceForUser; }
  get focusableTranslationSources() { return this.props.focusableTranslationSources; }
  get meaningTranslationsJoined() { return this.props.meaningTranslationsJoined; }
  get meaningStepNext() { return this.props.meaningStepNext; }
  get synonymChoiceLabel() { return this.props.synonymChoiceLabel; }
  get stepQuestionForStep() { return this.props.stepQuestionForStep; }
  get wordLevelDetailsPromptText() { return this.props.wordLevelDetailsPromptText; }
  get vocabularyUser() { return this.props.vocabularyUser; }
  get directSynonymIssueLabel() { return this.props.directSynonymIssueLabel; }
  get otherWordIssueLabel() { return this.props.otherWordIssueLabel; }
  get synonymContextText() { return this.props.synonymContextText; }
  get synonymReportPromptText() { return this.props.synonymReportPromptText; }
  get openDirectSynonymDetails() { return this.props.openDirectSynonymDetails; }
  get openOtherWordIssue() { return this.props.openOtherWordIssue; }
  get openComments() { return this.props.openComments; }
  get submitDirectSynonymReport() { return this.props.submitDirectSynonymReport; }

  sentenceChoiceContent(sentence) {
    const source = this.getSentenceSourceForUser(sentence);
    const hint = source?.textForUser ? source.textForUser(this.vocabularyUser) : source?.text;
    if (!hint) {
      return sentence.targetText;
    }
    return (
      <>
        {sentence.targetText}
        {' — '}
        <SourceInChoice>
          {hint}
        </SourceInChoice>
      </>
    );
  }

  renderNotesAndSubmitFooter() {
    return (
      <>
        {
          this.hasCurrentStepSelection &&
            <>
              <Text
                margin='14px 0 6px'
                fontSize='14px'
              >
                {this.notesPromptText}
              </Text>
              <NotesArea
                value={this.notes}
                onChange={(e) => {
                  this.notes = e.target.value;
                }}
                disabled={this.submitting}
                maxLength={500}
              />
            </>
        }
        {
          this.submitError &&
            <Text
              color='#b00020'
              margin='10px 0 0 0'
              fontSize={pixify('14px')}
            >
              {this.submitError}
            </Text>
        }
        <ActionsRows>
          {
            this.hasCurrentStepSelection &&
              <Button
                width='100%'
                onClick={this.submitMeaningFlag}
                background={Theme.blue}
                disabled={!this.canSubmitCurrentStep || this.submitting}
              >
                Submit
              </Button>
          }
          <Button
            width='100%'
            background='#e6e6e6'
            color='#191919'
            onClick={this.goBack}
            disabled={this.submitting}
          >
            Back
          </Button>
        </ActionsRows>
      </>
    );
  }

  render() {
    return (
      <FormContent>
        <BreadcrumbSlot>
            {
              this.step !== this.steps.THANK_YOU && this.step === this.steps.MEANING && this.reportingBreadcrumbWordSegment &&
                <BreadcrumbBar aria-label='Report context'>
                    <span title={this.reportingBreadcrumbWordSegment.title}>
                      {this.reportingBreadcrumbWordSegment.text}
                    </span>
                  </BreadcrumbBar>
            }
            {
              this.step !== this.steps.THANK_YOU && this.step === this.steps.WORD_LEVEL_DETAILS && this.wordLevelReasonCode && this.reportingBreadcrumbWordSegment &&
                <BreadcrumbBar aria-label='Report context'>
                    <span title={this.reportingBreadcrumbWordSegment.title}>
                      {this.reportingBreadcrumbWordSegment.text}
                    </span>
                    {
                      this.reportingBreadcrumbWordLevelIssueSegment &&
                        <>
                          <BreadcrumbSep aria-hidden>›</BreadcrumbSep>
                          <span title={this.reportingBreadcrumbWordLevelIssueSegment.title}>
                            {this.reportingBreadcrumbWordLevelIssueSegment.text}
                          </span>
                        </>
                    }
                  </BreadcrumbBar>
            }
            {
              this.step !== this.steps.THANK_YOU && this.step !== this.steps.MEANING && !(this.step === this.steps.WORD_LEVEL_DETAILS && this.wordLevelReasonCode) && this.reportingBreadcrumbMeaningSegment &&
                <BreadcrumbBar aria-label='Report context'>
                    <span title={this.reportingBreadcrumbWordSegment.title}>
                      {this.reportingBreadcrumbWordSegment.text}
                    </span>
                    <BreadcrumbSep aria-hidden>›</BreadcrumbSep>
                    <SourceInChoice title={this.reportingBreadcrumbMeaningSegment.title}>
                      {this.reportingBreadcrumbMeaningSegment.text}
                    </SourceInChoice>
                    {
                      this.reportingBreadcrumbSubstepSegment &&
                        <>
                          <BreadcrumbSep aria-hidden>›</BreadcrumbSep>
                          <span title={this.reportingBreadcrumbSubstepSegment.title}>
                            {this.reportingBreadcrumbSubstepSegment.text}
                          </span>
                        </>
                    }
                  </BreadcrumbBar>
            }
            {
              this.step !== this.steps.THANK_YOU && this.step !== this.steps.MEANING && !(this.step === this.steps.WORD_LEVEL_DETAILS && this.wordLevelReasonCode) && !this.reportingBreadcrumbMeaningSegment && this.reportingBreadcrumbWordSegment &&
                <BreadcrumbBar aria-label='Report context'>
                    <span title={this.reportingBreadcrumbWordSegment.title}>
                      {this.reportingBreadcrumbWordSegment.text}
                    </span>
                  </BreadcrumbBar>
            }
        </BreadcrumbSlot>
        <StepTitle>{this.stepQuestionForStep()}</StepTitle>
          {
            this.step === this.steps.SYNONYM_ISSUE &&
              <>
                <ChoicesRows>
                  <Choice
                    onClick={this.openDirectSynonymDetails}
                  >
                    {this.directSynonymIssueLabel}
                  </Choice>
                  <Choice
                    onClick={this.openOtherWordIssue}
                  >
                    {this.otherWordIssueLabel}
                  </Choice>
                </ChoicesRows>
                <ActionsRows>
                  <Button
                    width='100%'
                    background='#e6e6e6'
                    color='#636363'
                    onClick={this.close}
                  >
                    Cancel
                  </Button>
                </ActionsRows>
              </>
          }
          {
            this.step === this.steps.SYNONYM_DETAILS &&
              <>
                <Text
                  margin='0 0 14px'
                  fontSize='14px'
                  lineHeight={21}
                  textAlign='left'
                >
                  {this.synonymContextText}{' '}
                  <CommentLink onClick={this.openComments}>
                    comment section
                  </CommentLink>
                  .
                </Text>
                <Text
                  margin='0 0 6px'
                  fontSize='14px'
                >
                  {this.synonymReportPromptText}
                </Text>
                <NotesArea
                  value={this.notes}
                  onChange={(e) => {
                    this.notes = e.target.value;
                  }}
                  disabled={this.submitting}
                  maxLength={500}
                />
                {
                  this.submitError &&
                    <Text
                      color='#b00020'
                      margin='10px 0 0 0'
                      fontSize={pixify('14px')}
                    >
                      {this.submitError}
                    </Text>
                }
                <ActionsRows>
                  <Button
                    width='100%'
                    onClick={this.submitDirectSynonymReport}
                    background={Theme.blue}
                    disabled={!this.canSubmitCurrentStep || this.submitting}
                  >
                    Submit
                  </Button>
                  <Button
                    width='100%'
                    background='#e6e6e6'
                    color='#636363'
                    onClick={this.goBack}
                    disabled={this.submitting}
                  >
                    Back
                  </Button>
                </ActionsRows>
              </>
          }
          {
            this.step === this.steps.THANK_YOU &&
              <>
                <Text
                  margin='0 0 18px'
                  fontSize='16px'
                  lineHeight={23}
                >
                  {this.thankYouMessageText}
                </Text>
                <ActionsRows>
                  <Button
                    width='100%'
                    onClick={this.finishThankYou}
                    background={Theme.blue}
                  >
                    Done
                  </Button>
                </ActionsRows>
              </>
          }
          {
            this.step === this.steps.MEANING &&
              <>
                <ChoicesRows>
                  {
                    this.meaningsSorted.map(meaningOption => (
                      <Choice
                        key={meaningOption.id}
                        selected={this.selectedMeaning?.id === meaningOption.id}
                        onClick={() => this.selectMeaning(meaningOption)}
                      >
                        <SourceInChoice>
                          {this.meaningTranslationsJoined(meaningOption)}
                        </SourceInChoice>
                      </Choice>
                    ))
                  }
                  {
                    this.meaningFlagWordLevelIssues.map(([code, label]) => (
                      <Choice
                        key={code}
                        selected={this.wordLevelReasonCode === code}
                        onClick={() => this.selectWordLevel(code)}
                      >
                        {label}
                      </Choice>
                    ))
                  }
                </ChoicesRows>
                {
                  this.submitError &&
                    <Text
                      color='#b00020'
                      margin='12px 0 0 0'
                      fontSize={pixify('14px')}
                    >
                      {this.submitError}
                    </Text>
                }
                <ActionsRows>
                  <Button
                    width='100%'
                    onClick={this.meaningStepNext}
                    background={Theme.blue}
                    disabled={!this.hasFirstStepChoice || this.submitting}
                  >
                    Next
                  </Button>
                  <Button
                    width='100%'
                    background='#e6e6e6'
                    color='#636363'
                    onClick={this.close}
                    disabled={this.submitting}
                  >
                    Cancel
                  </Button>
                </ActionsRows>
              </>
          }
          {
            this.step === this.steps.WORD_LEVEL_DETAILS &&
              <>
                <Text
                  margin='0 0 6px'
                  fontSize='14px'
                >
                  {this.wordLevelDetailsPromptText()}
                </Text>
                <NotesArea
                  value={this.notes}
                  placeholder={
                    this.wordLevelReasonCode === 'other'
                      ? 'Required when you choose "Something else."'
                      : 'Optional.'
                  }
                  onChange={(e) => {
                    this.notes = e.target.value;
                  }}
                  disabled={this.submitting}
                  maxLength={500}
                />
                {
                  this.submitError &&
                    <Text
                      color='#b00020'
                      margin='10px 0 0 0'
                      fontSize={pixify('14px')}
                    >
                      {this.submitError}
                    </Text>
                }
                <ActionsRows>
                  <Button
                    width='100%'
                    onClick={this.submitWordLevel}
                    background={Theme.blue}
                    disabled={!this.canSubmitCurrentStep || this.submitting}
                  >
                    Submit
                  </Button>
                  <Button
                    width='100%'
                    background='#e6e6e6'
                    color='#636363'
                    onClick={this.goBack}
                    disabled={this.submitting}
                  >
                    Back
                  </Button>
                </ActionsRows>
              </>
          }
          {
            this.step === this.steps.REASONS &&
              <>
                <ChoicesRows>
                  {
                    this.selectedMeaning && this.focusableTranslationSources(this.selectedMeaning).length > 0 &&
                      <Choice
                          selected={this.focusedTranslationSourceIds.length > 0}
                          onClick={this.openTranslationPickStep}
                        >
                          {this.meaningFlagHubFocusChoices.translations}
                        </Choice>
                  }
                  {
                    this.props.reportSentences.length > 0 &&
                      <Choice
                        selected={false}
                        onClick={this.openExampleSentencePickStep}
                      >
                        {this.meaningFlagHubFocusChoices.example_sentence}
                      </Choice>
                  }
                  {
                    (this.selectedMeaning?.synonyms || []).length > 0 &&
                      <Choice
                        selected={false}
                        onClick={this.openSynonymPickStep}
                      >
                        {this.meaningFlagHubFocusChoices.example_synonym}
                      </Choice>
                  }
                  {
                    this.meaningFlagMeaningIssues.map(([code, label]) => (
                      <Choice
                        key={code}
                        selected={this.selectedReasonCodes.includes(code)}
                        onClick={() => this.toggleReason(code)}
                      >
                        {label}
                      </Choice>
                    ))
                  }
                </ChoicesRows>
                {this.renderNotesAndSubmitFooter()}
              </>
          }
          {
            this.step === this.steps.TRANSLATION_SOURCES &&
              <>
                <ChoicesRows>
                  {
                    this.selectedMeaning && this.focusableTranslationSources(this.selectedMeaning).map(ts => (
                      <Choice
                        key={ts.id}
                        selected={this.focusedTranslationSourceIds.includes(ts.id)}
                        onClick={() => this.setFocusedTranslationSource(ts.id)}
                      >
                        <SourceInChoice>
                          {this.getTranslationSourceDisplayText(ts)}
                        </SourceInChoice>
                      </Choice>
                    ))
                  }
                </ChoicesRows>
                {this.renderNotesAndSubmitFooter()}
              </>
          }
          {
            this.step === this.steps.EXAMPLE_SENTENCES &&
              <>
                <ChoicesRows>
                  {
                    this.props.reportSentences.map(sentence => (
                      <Choice
                        key={sentence.id}
                        selected={this.focusedSentenceIds.includes(sentence.id)}
                        onClick={() => this.setFocusedSentence(sentence.id)}
                      >
                        {this.sentenceChoiceContent(sentence)}
                      </Choice>
                    ))
                  }
                </ChoicesRows>
                {this.renderNotesAndSubmitFooter()}
              </>
          }
          {
            this.step === this.steps.SYNONYMS &&
              <>
                <ChoicesRows>
                  {
                    (this.selectedMeaning?.synonyms || []).map(syn => (
                      <Choice
                        key={syn.id}
                        selected={this.focusedSynonymIds.includes(syn.id)}
                        onClick={() => this.setFocusedSynonym(syn.id)}
                      >
                        {this.synonymChoiceLabel(syn)}
                      </Choice>
                    ))
                  }
                </ChoicesRows>
                {this.renderNotesAndSubmitFooter()}
              </>
          }
      </FormContent>
    );
  }
}

export default ReportMeaningsForm;
